import { useState, useEffect, useCallback, useRef } from "react";
import AgoraRTC, {AgoraRTCScreenShareProvider} from "agora-rtc-sdk-ng";
import $ from "jquery";
import { appConfig } from "../../common/constants";

export default function useAgora(channelName) {
  // Debug logging function
  const debugLog = (message, ...args) => {
    console.log(`[Agora Debug - ${channelName}]`, message, ...args);
  };

  // Refs for stable references
  const clientRef = useRef(null);
  const localTracksRef = useRef({
    videoTrack: null,
    audioTrack: null,
  });

  // State hooks
  const [client, setClient] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(undefined);
  const [localAudioTrack, setLocalAudioTrack] = useState(undefined);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [joinState, setJoinState] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenTrack, setScreenTrack] = useState(null); // To manage the screen-sharing track
const [screenClient, setScreenClient] = useState(null); // To manage the screen-sharing client
  const [credentials, setCredentials] = useState({
    appId: "",
    token: "",
  });
  const [error, setError] = useState(null);

  // Fetch data utility with enhanced logging
  const fetchData = useCallback(async (queryUrl) => {
    debugLog(`Fetching data from URL: ${queryUrl}`);
    try {
      debugLog("Env Type:", process.env.REACT_APP_ENVTYPE);
      debugLog("Base URL:", appConfig.BaseUrl);

      const fullUrl = `${appConfig.BaseUrl}${queryUrl}`;
      debugLog("Full URL:", fullUrl);

      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      debugLog("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        debugLog("Error response text:", errorText);
        throw new Error(`Failed to fetch: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      debugLog("Fetched data:", data);
      return data;
    } catch (error) {
      debugLog("Fetch error:", error);
      setError(error.message);
      return null;
    }
  }, []);

  // Create local tracks with error handling
  const createLocalTracks = useCallback(async (audioConfig, videoConfig) => {
    debugLog("Creating local tracks");
    try {
      // Initialize with default configs if not provided
      const defaultVideoConfig = {
        encoderConfig: "1080p",
        facingMode: "user",
      };
      const defaultAudioConfig = {
        AEC: true,
        AGC: true,
      };

      const [microphoneTrack, cameraTrack] =
        await AgoraRTC.createMicrophoneAndCameraTracks(
          audioConfig || defaultAudioConfig,
          videoConfig || defaultVideoConfig
        );

      // Verify tracks are created
      if (!cameraTrack || !microphoneTrack) {
        throw new Error("Failed to create local tracks");
      }

      debugLog("Tracks created successfully:", {
        video: cameraTrack.getTrackId(),
        audio: microphoneTrack.getTrackId(),
      });
      // Initialize track states
      setLocalAudioTrack(microphoneTrack);
      setLocalVideoTrack(cameraTrack);

      // Update refs
      localTracksRef.current = {
        audioTrack: microphoneTrack,
        videoTrack: cameraTrack,
      };
      console.log("TRIGGERED ===>>>", localTracksRef.current);
      return [microphoneTrack, cameraTrack];
    } catch (error) {
      debugLog("Error creating local tracks:", error);
      setError(`Failed to create media tracks: ${error.message}`);
      throw error;
    }
  }, []);

  // Leave channel with comprehensive cleanup
  const leave = useCallback(async () => {
    debugLog("Leaving channel");
    try {
      // Stop and close local tracks
      if (localTracksRef.current.audioTrack) {
        localTracksRef.current.audioTrack.stop();
        localTracksRef.current.audioTrack.close();
      }
      if (localTracksRef.current.videoTrack) {
        localTracksRef.current.videoTrack.stop();
        localTracksRef.current.videoTrack.close();
      }

      // Reset local tracks
      setLocalAudioTrack(undefined);
      setLocalVideoTrack(undefined);

      // Leave Agora client
      if (clientRef.current) {
        await clientRef.current.leave();
      }

      // Reset states
      setRemoteUsers([]);
      setJoinState(false);
      $("#local-player-text").text("");

      // Clear references
      clientRef.current = null;
      localTracksRef.current = { videoTrack: null, audioTrack: null };
      localStorage.removeItem("channel_id");
    } catch (error) {
      debugLog("Leave channel error:", error);
      setError(error.message);
    }
  }, []);

  // Join channel with comprehensive logging and error handling
  const join = useCallback(
    async (appid, channel, token, uid) => {
      debugLog("Attempting to join channel");
      try {
        if (!appid || !channel) {
          console.log("AppID and channel name are required");
        }
        clientRef.current = AgoraRTC.createClient({
          mode: "rtc",
          codec: "vp8",
        });
        setClient(clientRef.current);

        // Get credentials
        const finalAppId = appid || process.env.REACT_APP_AGORA_APP_ID;
        const finalToken = token || null;

        debugLog("Joining channel with AppID:", finalAppId);
        await clientRef.current.join(
          finalAppId,
          channel,
          finalToken,
          uid || null,
          localStorage.getItem("loggedInUser")
        );

        // Create and publish tracks
        const [audioTrack, videoTrack] = await createLocalTracks();
        if (audioTrack && videoTrack) {
          await clientRef.current.publish([audioTrack, videoTrack]);
          window.client = client;
          window.videoTrack = videoTrack;
          setJoinState(true);
        }
      } catch (error) {
        debugLog("Join channel error:", error);
        setError(error.message);
        throw error;
      }
    },
    [leave, createLocalTracks]
  );

  const callAgoraCred = async () => {
    var data = await fetchData("agora?env=" + process.env.REACT_APP_ENVTYPE);
    if (data) {
      setCredentials((prev) => ({ ...prev, appId: data.appID }));
      callGetToken(data.appID);
    }
  };
  const callGetToken = async (appId) => {
    var data1 = await fetchData(
      "agora?env=" + process.env.REACT_APP_ENVTYPE + "&channel=" + channelName
    );
    if (data1) {
      setCredentials((prev) => ({ ...prev, token: data1.token }));
      join(appId, channelName, data1.token);
    }
  };

  const screenshare = useCallback(async () => {
    debugLog("Toggling screen share");
    try {
      if (isScreenSharing) {
        // stop screen sharing
        debugLog("Stopping screen share");
        if (screenTrack) {
          await screenClient.unpublish(screenTrack);
          screenTrack.stop();
          screenTrack.close();
          setScreenTrack(null);
        }
        if (screenClient) {
          await screenClient.leave();
          setScreenClient(null);
        }
        setIsScreenSharing(false);
        debugLog("Screen sharing stopped");
      } else {
        // start screen sharing
        debugLog("Starting screen share");
        const newScreenTrack = await AgoraRTC.createScreenVideoTrack(
          {
            encoderConfig: "1080p_1",
            optimizationMode: "detail",
            screenSourceType: "screen",
          }
        );
  
        if (newScreenTrack) {
          const newScreenClient = AgoraRTC.createClient({
            mode: "rtc",
            codec: "vp8",
          });
  
          const screenUid = Math.floor(Math.random() * 1000000);
          await newScreenClient.join(credentials.appId, channelName, credentials.token, screenUid);
          await newScreenClient.publish(newScreenTrack);
  
          newScreenTrack.on("track-ended", async () => {
            debugLog("Screen sharing ended by user");
            await newScreenClient.unpublish(newScreenTrack);
            newScreenTrack.stop();
            newScreenTrack.close();
            setScreenTrack(null);
            await newScreenClient.leave();
            setScreenClient(null);
            setIsScreenSharing(false);
          });
  
          setScreenTrack(newScreenTrack);
          setScreenClient(newScreenClient);
          setIsScreenSharing(true);
          debugLog("Screen sharing started successfully");
        }
      }
    } catch (error) {
      if (error.name === "NotAllowedError" || error.message.includes("Permission denied")) {
        debugLog("Screen sharing was canceled by the user");
      } else {
        debugLog("Screen sharing error:", error);
        setError("Failed to start screen sharing");
      }
    }
  }, [isScreenSharing, screenTrack, screenClient, credentials, channelName]);

  useEffect(() => {
    if (!client) return;

    debugLog("Setting up client event listeners");

    // Initial remote users
    setRemoteUsers(client.remoteUsers);

    // Event handlers
    const handleUserPublished = async (user, mediaType) => {
      debugLog("User published:", user, mediaType);
      try {
        await client.subscribe(user, mediaType);
        setRemoteUsers((remoteUsers) => Array.from(client.remoteUsers));
      } catch (error) {
        debugLog("User publish error:", error);
      }
    };

    const handleUserUnpublished = (user) => {
      debugLog("User unpublished:", user);
      setRemoteUsers((remoteUsers) => Array.from(client.remoteUsers));
    };

    const handleUserJoined = (user) => {
      debugLog("User joined:", user);
      setRemoteUsers((remoteUsers) => Array.from(client.remoteUsers));
    };

    const handleUserLeft = (user) => {
      debugLog("User left:", user);
      setRemoteUsers((remoteUsers) => Array.from(client.remoteUsers));
    };

    // Attach event listeners
    client.on("user-published", handleUserPublished);
    client.on("user-unpublished", handleUserUnpublished);
    client.on("user-joined", handleUserJoined);
    client.on("user-left", handleUserLeft);

    // Cleanup listeners on unmount
    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-unpublished", handleUserUnpublished);
      client.off("user-joined", handleUserJoined);
      client.off("user-left", handleUserLeft);
    };
  }, [client]);

  useEffect(() => {
    return () => {
      leave();
    };
  }, [leave]);

  useEffect(() => {
    if (channelName) {
      callAgoraCred();
    }
  }, []);

  const toggleVideo = async () => {
    debugLog("Toggling video");
    try {
      if (!localTracksRef.current.videoTrack) {
        debugLog("No video track available");
        return;
      }
  
      const currentTrack = localTracksRef.current.videoTrack;
      const newEnabled = !currentTrack.enabled;
  
      debugLog("Setting video enabled:", newEnabled);
      await currentTrack.setEnabled(newEnabled);

      setIsVideoMuted(!newEnabled);
    } catch (error) {
      debugLog("Toggle video error:", error);
      setError("Failed to toggle video");
    }
  };

  return {
    localAudioTrack,
    localVideoTrack,
    toggleVideo,
    joinState,
    leave,
    join,
    screenshare,
    remoteUsers,
    isMinimized,
    setIsMinimized,
    error,
    credentials,
    callAgoraCred,
    isAudioMuted,
    setIsAudioMuted,
    isVideoMuted,
    setIsVideoMuted,
    isScreenSharing,
    client,
    localTracksRef,
    screenTrack,
  };
}
